import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Chip, ListItem, Stack, Typography } from "@mui/material";
import { formatMoney } from "@bw/shared/utils";
import type { BookingResponse } from "@bw/shared/types/generated";

/**
 * One row of the player's CROSS-FACILITY bookings list: the facility name · the
 * court · start time · scoped amount · a host-only Cancel action (when the
 * booking is still cancellable). An accepted participant gets a truthful
 * "Going" state and an em dash for redacted money. Unlike the per-facility row,
 * this surfaces the FACILITY name (the list spans many venues) and court inline.
 *
 * The facility name is RESOLVED from the real facility endpoint and passed in as
 * `facilityName`; when it has not resolved yet (or the venue errored), the prop is
 * undefined and the row shows a neutral honest label ("Facility") — it never
 * renders a raw UUID and never fabricates a venue name.
 *
 * Every other value comes straight from the BookingResponse — start time,
 * amount_charged, the server-resolved court_name, and the cancelled state.
 * `display_name` is the booked-for player and is never used as a court label.
 * A cancelled booking shows its server-set refund_amount and is no longer
 * cancellable.
 *
 * Cancellability is SERVER state, not a client guess. The backend rejects a cancel
 * on a terminal booking (completed / no-show / a past start_time), so the row must
 * NOT offer a Cancel button the server will refuse. We gate conservatively on the
 * only honest signal we have: status === "confirmed" AND a future start_time.
 */

/** Venue-local full booking range, best-effort. */
function whenLabel(startTime: string, endTime: string): string {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
    const start = new Date(startTime);
    const end = new Date(endTime);
    if (!Number.isNaN(end.getTime())) return formatter.formatRange(start, end);
    return formatter.format(start);
  } catch {
    return startTime;
  }
}

/**
 * The Material date badge parts (day number + short month) for a booking start.
 * Purely decorative — it visually anchors the row while the full,
 * authoritative date lives in the row's sub text (so a screen reader still hears
 * the complete date). Returns null when the start_time is unparseable, so the row
 * degrades to no badge rather than rendering a nonsense tile. */
function dateBadge(startTime: string): { day: string; month: string } | null {
  const d = new Date(startTime);
  if (Number.isNaN(d.getTime())) return null;
  return {
    day: new Intl.DateTimeFormat("en-US", { day: "numeric" }).format(d),
    month: new Intl.DateTimeFormat("en-US", { month: "short" })
      .format(d)
      .toUpperCase(),
  };
}

export function CalendarBookingRow({
  booking,
  facilityName,
  isHost,
  onCancel,
}: {
  booking: BookingResponse;
  /** Resolved facility display name, or undefined when not yet known. */
  facilityName: string | undefined;
  /** Whether the authenticated viewer owns this booking. The backend remains
   *  authoritative; this only keeps host-only cancellation out of guest UI. */
  isHost: boolean;
  onCancel: (booking: BookingResponse) => void;
}) {
  const isCancelled = booking.status === "cancelled";
  const isCancellationPending = booking.cancellation_recovery_pending === true;
  const currency = booking.currency ?? "USD";
  const court = booking.court_name?.trim() || "Court";
  // Prefer the live facility read, then the server-authored booking snapshot.
  // Neither path exposes a raw facility UUID or invents a venue name.
  const venue = facilityName || booking.facility_name?.trim() || "Facility";
  const playerCount = Math.max(1, booking.player_count);

  // Cancellable only when the server-set status is "confirmed" AND the booking
  // has not started yet. A terminal/past booking is rejected server-side, so we
  // never render a Cancel button the backend will refuse. `Date.parse` returns
  // NaN for an unparseable start_time; treat that as not-future (no Cancel).
  const startMs = Date.parse(booking.start_time);
  const isFuture = !Number.isNaN(startMs) && startMs > Date.now();
  const isCancellable =
    isHost &&
    booking.status === "confirmed" &&
    !isCancellationPending &&
    isFuture;

  // The detail hub (roster / check-in QR / host add-remove) is reachable from
  // EVERY row — upcoming, cancelled, or past — so a row is never a dead end.
  // Viewability is navigation, independent of Cancel/terminal state.
  const detailHref = `/book/${booking.facility_id}/bookings/${booking.booking_id}`;

  // Date tile — decorative (aria-hidden); the authoritative date is
  // in the row sub below, so the SR experience is unchanged.
  const badge = dateBadge(booking.start_time);

  return (
    <ListItem
      component="li"
      data-cancelled={isCancelled || undefined}
      sx={{ px: { xs: 0.5, sm: 1 }, py: 0.75 }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ width: "100%", minWidth: 0, alignItems: "center" }}
      >
        {badge ? (
          <Box
            aria-hidden="true"
            sx={{
              flex: "0 0 auto",
              width: 44,
              py: 0.5,
              borderRadius: 2,
              bgcolor: "action.hover",
              textAlign: "center",
              lineHeight: 1.1,
            }}
          >
            <Typography sx={{ fontSize: "0.625rem", fontWeight: 800, color: "primary.main" }}>
              {badge.month}
            </Typography>
            <Typography sx={{ fontSize: "1.0625rem", fontWeight: 700 }}>
              {badge.day}
            </Typography>
          </Box>
        ) : null}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: "0.9375rem", fontWeight: 700 }}>
            {venue}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {court} · {whenLabel(booking.start_time, booking.end_time)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <span>{formatMoney(booking.amount_charged, currency)}</span> ·{" "}
            <span>{`${playerCount} ${playerCount === 1 ? "player" : "players"}`}</span>
          </Typography>
        </Box>
        <Stack spacing={0.25} sx={{ flex: "0 0 auto", alignItems: "flex-end" }}>
          <Button
            component={RouterLink}
            size="small"
            variant="outlined"
            color="inherit"
            to={detailHref}
            sx={{ borderRadius: 999, minWidth: 0, px: 1.5 }}
          >
            View
          </Button>
          {isCancelled ? (
            <Chip
              size="small"
              color="default"
              label={
                booking.refund_amount != null
                  ? `Cancelled · ${formatMoney(booking.refund_amount, currency)} refunded`
                  : "Cancelled"
              }
            />
          ) : isCancellationPending ? (
            <Chip
              size="small"
              label="Cancellation processing"
              color="warning"
            />
          ) : isCancellable ? (
            <Button
              size="small"
              variant="text"
              color="error"
              onClick={() => onCancel(booking)}
            >
              Cancel
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {booking.status === "confirmed" && isFuture
                ? "Going"
                : terminalLabel(booking.status)}
            </Typography>
          )}
        </Stack>
      </Stack>
    </ListItem>
  );
}

/** Human-readable terminal/non-cancellable state for a booking row. */
function terminalLabel(status: string): string {
  switch (status) {
    case "completed":
      return "Completed";
    case "no_show":
    case "no-show":
      return "No-show";
    default:
      return "Past";
  }
}
